import { applyCallConfirmed, onFileExpiryFrom } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { opsCancelBooking, markNoShowBooking, type NoShowResult } from '@/domain/opsCancelFlow';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { recordContactAttempt } from '@/domain/sequenceFlow';
import { isShortNoticeBooking } from '@/domain/confirmationWorklist';
import { eventBus } from '@/events/eventBus';
import type { Booking, ConfirmationCall, ConfirmationTouch } from '@/domain/types';

/**
 * Confirmation outcomes (Prompt G, item 10). Every due confirmation resolves to
 * exactly one of these — nothing falls through silently.
 */
export type ConfirmationOutcome = 'confirmed' | 'rescheduled' | 'cancelled' | 'no-answer';

export interface ConfirmationOutcomeResult {
  ok: boolean;
  completion?: Promise<boolean>;
  outcome?: ConfirmationOutcome;
  bookingId?: string;
  /** rescheduled: the UI must open the G1 reschedule flow (pick a new slot). */
  needsRescheduleUI?: boolean;
  /** cancelled with a paid deposit: the deposit that now sits in the money queue. */
  surfacedDepositId?: string;
  /** cancelled only: why it was refused (nothing was mutated). */
  error?: 'missing-reason' | 'already-cancelled' | 'no-booking' | 'not-cancellable' | 'past-booking';
}

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/**
 * A dateless / on-file deposit is valid 12 months from the instant it went on
 * file (vault Diagram 06, tv-bli). Deterministic 12-month add, no Date.now().
 * The ONE rule is @atink/domain onFileExpiryFrom (shared with the server's cancel
 * and "No date" intents, tv-2na.11.13); this name stays for existing callers.
 */
export function onFileExpiryIso(fromIso: string): string {
  return onFileExpiryFrom(fromIso);
}

/** Call outcome -> the persisted touch outcome (booking_confirmation_touches CHECK). */
const TOUCH_OUTCOME: Partial<Record<ConfirmationOutcome, ConfirmationTouch['outcome']>> = {
  confirmed: 'confirmed',
  'no-answer': 'no-response',
  rescheduled: 'rescheduled',
};

function withTouch(booking: Booking, touch: ConfirmationTouch): Booking {
  return { ...booking, confirmationTouches: [...(booking.confirmationTouches ?? []), touch] };
}

/**
 * Resolve a confirmation call. A call outcome is CONFIRMATION only: it never
 * changes the booking's lifecycle status (DECIDED 23 Sep, W4). Every outcome
 * appends a touch to the booking (persisted to booking_confirmation_touches
 * through the booking write path) and records the outcome on the call.
 *
 *   confirmed    confirmedAt (applyCallConfirmed). An unpaid hold stays held and
 *                needs a deposit; it is not made firm.
 *   no-answer    advances the sequence counter; the 3rd escalates to Ops.
 *   rescheduled  the move itself is completed by the reschedule flow.
 *   cancelled    requires a typed reason; routed through the Ops cancel flow.
 */
export function resolveConfirmationOutcome(
  callId: string,
  outcome: ConfirmationOutcome,
  opts?: { by?: string; at?: string; reason?: string },
): ConfirmationOutcomeResult {
  const brain = useBrainStore.getState();
  const call = brain.confirmationCalls[callId];
  if (!call) return { ok: false };
  const booking = brain.bookings[call.bookingId];
  if (!booking) return { ok: false };
  const at = opts?.at ?? nowIso();
  const by = opts?.by ?? 'system';

  if (outcome === 'cancelled') {
    // Ops cancel from the Confirmations flow: typed reason mandatory, checked
    // BEFORE anything is recorded so a refused cancel leaves no trace.
    const reason = opts?.reason?.trim() ?? '';
    if (!reason) return { ok: false, error: 'missing-reason' };
    if (booking.status === 'cancelled') return { ok: false, error: 'already-cancelled' };
    // W5: the Ops cancel applies the shared 7-day split (applyCancelSplit): the
    // slot frees (cancelled + reason/by/at), a paid deposit forfeits inside 7 days
    // or goes on file outside, and the call id stamps a 'cancelled' touch (the
    // touches CHECK now allows it). Served build: one atomic intent.
    const res = opsCancelBooking({ bookingId: booking.id, reason, by, at, callId });
    if (!res.ok) return { ok: false, outcome, bookingId: booking.id, error: res.error };
    // The call record itself still write-throughs: an ad-hoc call minted a
    // moment ago (ensureConfirmationCall) may not have reached the DB when the
    // intent runs, so its outcome must ride its own PUT (the intent's update of
    // an existing call row is the same value — idempotent).
    const recordCall = () => brain.upsertConfirmationCall({ ...call, outcome });
    const completion = res.completion?.then((ok) => { if (ok) recordCall(); return ok; });
    if (!completion) recordCall();
    return { ok: true, outcome, bookingId: booking.id, surfacedDepositId: res.surfacedDepositId, completion };
  }

  // Record the resolution on the call, and the touch on the booking.
  brain.upsertConfirmationCall({ ...call, outcome });
  const touched = withTouch(booking, {
    at,
    channel: call.kind === 'sms' ? 'sms' : 'call',
    outcome: TOUCH_OUTCOME[outcome]!,
    by,
  });

  if (outcome === 'confirmed') {
    // confirmedAt only — never the status. The shared confirm hook still sends
    // the waiver + ICS appointment summary (tv-iui).
    brain.upsertBooking(applyCallConfirmed(touched, at));
    onBookingConfirmed(booking.id);
    return { ok: true, outcome, bookingId: booking.id };
  }

  if (outcome === 'no-answer') {
    // Re-arm the existing 24h sequence window (reuse, do not fork) so the item
    // resurfaces as active work instead of disappearing.
    recordContactAttempt(booking.customerId);
    // Advance the per-booking cadence counter: drives which paired-day sequence
    // is next due. On the 3rd unanswered sequence, escalate to Ops exactly once -
    // stamp the flag the Ops worklist consumes and emit it (tv-k6t).
    const attempts = (booking.confirmationAttempts ?? 0) + 1;
    const escalate = attempts >= 3 && !booking.opsEscalatedAt;
    brain.upsertBooking({
      ...touched,
      confirmationAttempts: attempts,
      ...(escalate ? { opsEscalatedAt: at } : {}),
    });
    if (escalate) {
      eventBus.publish('confirmation:escalated', {
        bookingId: booking.id,
        customerId: booking.customerId,
        studioId: booking.studioId,
        at,
      });
    }
    return { ok: true, outcome, bookingId: booking.id };
  }

  // rescheduled: the move itself is completed on the calendar (reason picker +
  // slot pick). Only the touch is recorded here. Signal the UI to open the G1 flow.
  brain.upsertBooking(touched);
  return { ok: true, outcome, needsRescheduleUI: true, bookingId: booking.id };
}

/**
 * Idempotent: every worklist row must open the work modal, including a chase
 * row that never had a confirmation touch scheduled (only overdue via the
 * day-of catch-up, not a stale call). Returns the existing ConfirmationCall id
 * for `bookingId` if one exists; otherwise mints a deterministic one - that
 * new record IS the touch about to be made - and returns its id. Re-opening
 * the same call-less row never creates a second call (stable id, upsert).
 * Returns '' (and persists nothing) when the booking does not exist - never
 * leave a junk call with no booking behind it.
 */
export function ensureConfirmationCall(
  bookingId: string,
  opts?: { at?: string; kind?: 'sms' | 'call' },
): string {
  const brain = useBrainStore.getState();
  const existing = Object.values(brain.confirmationCalls).find((c) => c.bookingId === bookingId);
  if (existing) return existing.id;

  const booking = brain.bookings[bookingId];
  if (!booking) return '';

  const id = `conf-adhoc-${bookingId}`;
  const call: ConfirmationCall = {
    id,
    bookingId,
    customerId: booking.customerId,
    scheduledFor: opts?.at ?? nowIso(),
    kind: opts?.kind ?? 'call',
    recommendedAction: 'human-preferred',
    projectedCostAud: 0,
  };
  brain.upsertConfirmationCall(call);
  return id;
}

/**
 * Live booking statuses that a short-notice auto-confirm can act on. A cancelled,
 * superseded, no-show or completed booking is never re-confirmed.
 */
const CONFIRMABLE_STATUSES = new Set<Booking['status']>(['held', 'pending-deposit', 'confirmed']);

/**
 * Short-notice / gap-fill auto-confirm at booking (vault Diagram 06, tv-bli). A
 * gap-fill client has just engaged, so the booking is confirmed at creation and
 * skips the pre-day cadence entirely. Stamps confirmedAt (never the status: a
 * hold is not made solid) and fires the shared confirm hook (waiver + ICS) when
 * the booking is live, not already confirmed, and `isShortNoticeBooking`; otherwise a no-op returning
 * false (a normal-lead booking keeps its cadence). "Now" is the sim clock.
 *
 * Runtime gap-fill bookings are minted in the queue (Wave 0); wiring this into
 * that creation path is left to Wave 0 - this is the unit-tested hook.
 */
export function autoConfirmIfShortNotice(bookingId: string, opts?: { at?: string }): boolean {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return false;
  if (!CONFIRMABLE_STATUSES.has(booking.status)) return false; // not live
  if (booking.confirmedAt) return false; // already confirmed - do not double-confirm
  if (!isShortNoticeBooking(booking)) return false; // normal lead - keep the cadence
  const at = opts?.at ?? nowIso();
  // Confirmation only, never the lifecycle status (W4 contract): a short-notice
  // hold stays held (not solid) until a deposit firms it — needsDeposit.
  brain.upsertBooking(applyCallConfirmed(booking, at));
  onBookingConfirmed(bookingId);
  return true;
}

/**
 * Day-of no-show marking (Prompt G, item 10). W5b #1 (tv-2na.12.17, Q6): goes
 * through the ONE cancel intent (mode 'no-show') — the booking flips to no-show
 * and any paid money is forfeited with the typed reason on the ledger note
 * (L1, 17 Jul), falling back to the fixed 8h-rule note. A booking that has not
 * started yet, or has arrived, is refused (the result says why).
 */
export function markNoShow(
  bookingId: string,
  opts?: { by?: string; at?: string; note?: string },
): NoShowResult {
  return markNoShowBooking({
    bookingId,
    reason: opts?.note?.trim() || 'No-show forfeiture (8h rule)',
    by: opts?.by ?? 'system',
    source: 'floor',
    ...(opts?.at ? { at: opts.at } : {}),
  });
}
